import React, { useState } from 'react';
import gettext from '@eb/gettext';
import { Modal } from '@eb/eds-modal';
import { Illustration } from '@eb/eds-illustration';
import { Divider } from '@eb/eds-divider';
import { BlueMoneyIllustration } from '@eb/eds-iconography';
import { FeeBreakdownInterface } from '../../constants/pricing';

import './feeBreakdownModal.scss';

const ItemRow = ({
    label,
    value,
}: {
    label: string;
    value: string | undefined;
}) => (
    <div className="eds-l-pad-bot-1">
        <div className="eds-g-cell eds-g-cell-6-12">{label}</div>
        <div className="eds-g-cell eds-g-cell-6-12">
            <div className="eds-align--right">{value}</div>
        </div>
    </div>
);

const ItemHeading = ({ children }: { children: string }) => (
    <div className="eds-text-weight--heavy eds-l-pad-bot-2">{children}</div>
);

const FeeBreakdown = ({
    attendeeTotal,
    passOnFees,
    processingFee,
    processingFeeTax,
    serviceFee,
    serviceFeeTax,
    ticketRevenue,
}: FeeBreakdownInterface) => {
    const preFeeSymbol = passOnFees ? '' : '-';
    const priceValue = passOnFees ? ticketRevenue : attendeeTotal;

    const feeRows = (
        <div>
            <ItemRow label={gettext('Ticket price')} value={priceValue} />
            <ItemRow
                label={gettext('Service fee')}
                value={`${preFeeSymbol}${serviceFee}`}
            />
            <ItemRow
                label={gettext('Processing fee')}
                value={`${preFeeSymbol}${processingFee}`}
            />
            {serviceFeeTax && (
                <ItemRow
                    label={gettext('Service fee tax')}
                    value={`${preFeeSymbol}${serviceFeeTax}`}
                />
            )}
            {processingFeeTax && (
                <ItemRow
                    label={gettext('Processing fee tax')}
                    value={`${preFeeSymbol}${processingFeeTax}`}
                />
            )}
            <div className="eds-l-pad-vert-1">
                <Divider />
            </div>
        </div>
    );

    return (
        <section className="eds-l-pad-top-4">
            <div className="eds-text-color--grey-600 eds-text-bm">
                <ItemHeading>{gettext('Cost to buyer')}</ItemHeading>
                <div className="eds-l-pad-bot-2">
                    {passOnFees && feeRows}
                    <ItemRow label={gettext('Total')} value={attendeeTotal} />
                </div>
                <ItemHeading>{gettext('Your payout')}</ItemHeading>
                <div className="eds-l-pad-bot-2">
                    {!passOnFees && feeRows}
                    <ItemRow
                        label={gettext('Ticket revenue')}
                        value={ticketRevenue}
                    />
                </div>
            </div>
        </section>
    );
};

const IllustrationComponent = (
    <div className="modal-illustration">
        <div className="modal-illustration__content blue-money-modal-content">
            <Illustration
                type={<BlueMoneyIllustration />}
                height="420px"
                width="285px"
            />
        </div>
    </div>
);

interface FeeBreakdownModalProps extends FeeBreakdownInterface {
    children: Function;
}

const FeeBreakdownModal = ({
    attendeeTotal,
    children,
    passOnFees,
    processingFee,
    processingFeeTax,
    serviceFee,
    serviceFeeTax,
    ticketRevenue,
}: FeeBreakdownModalProps) => {
    const [isShown, setShown] = useState(false);

    return (
        <div className="eds-g-grid pricing-calculator--fee-modal">
            <div className="eds-g-cell eds-g-cell-1-1">
                <Modal
                    isShown={isShown}
                    illustration={IllustrationComponent}
                    onClose={() => setShown(false)}
                    onPrimaryAction={() => setShown(false)}
                    primaryText={gettext('Done')}
                    primaryType="link"
                >
                    <FeeBreakdown
                        passOnFees={passOnFees}
                        serviceFee={serviceFee}
                        processingFee={processingFee}
                        processingFeeTax={processingFeeTax}
                        attendeeTotal={attendeeTotal}
                        ticketRevenue={ticketRevenue}
                        serviceFeeTax={serviceFeeTax}
                    />
                </Modal>
                {children(() => setShown(true))}
            </div>
        </div>
    );
};

export default FeeBreakdownModal;
